import { useDocItems } from '@modules/docs/providers';
import { ListItemText, MenuItem, MenuList, TextField } from '@mui/material';
import { Stack } from '@mui/system';
import { useMemo, useState } from 'react';
import { useThrottle } from 'react-use';
import { DocItem } from 'src/@types/docs';
import { useHotkeys } from '../../hooks/useHotkeys';
import { CustomPopover } from '../CustomPopover';
import { MenuProps } from './types';

export const MentionDocMenu = ({ onClose, suggestion }: MenuProps) => {
  const { editor, clientRect, range } = suggestion;
  const [inputValue, setInputValue] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const throttledValue = useThrottle(inputValue, 500);

  const { docs } = useDocItems();

  const handleSelectDoc = (doc: DocItem) => {
    editor.chain().focus().mentionDoc({ range, id: doc._id }).run();
    onClose();
  };

  const filteredDocs = useMemo(() => {
    if (!throttledValue) return docs;
    const search = throttledValue.toLowerCase();
    const filteredDocs = docs?.filter((doc) =>
      doc.title.toLowerCase().includes(search),
    );
    return filteredDocs;
  }, [docs, throttledValue]);

  const totalItems = filteredDocs?.length || 0;

  const handleKeyDown = useHotkeys(
    editor,
    {
      ArrowUp: (e) => {
        setSelectedIndex((selectedIndex + totalItems - 1) % totalItems);
        e.preventDefault();
      },
      ArrowDown: (e) => {
        setSelectedIndex((selectedIndex + 1) % totalItems);
        e.preventDefault();
      },
      Enter: (e) => {
        handleSelectDoc(docs![selectedIndex]);
        e.stopPropagation();
        e.preventDefault();
      },
      Escape: () => {
        onClose();
      },
    },
    [totalItems, selectedIndex, setSelectedIndex, docs],
  );

  return (
    <CustomPopover
      editor={editor}
      clientRect={clientRect}
      paperProps={{
        sx: {
          maxHeight: '330px',
          width: '280px',
          overflowY: 'auto',
        },
      }}
    >
      <Stack>
        <TextField
          label=""
          autoFocus
          variant="outlined"
          fullWidth
          size="small"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Search for a document"
          sx={{ p: 1, pt: 2 }}
          onKeyDown={(e) => handleKeyDown(e as any)}
        />
        <MenuList>
          {filteredDocs?.map((doc, index) => (
            <MenuItem
              key={`link-doc-item-${index}`}
              onClick={() => handleSelectDoc(doc)}
              selected={selectedIndex === index}
            >
              <ListItemText>{doc.title}</ListItemText>
            </MenuItem>
          ))}
        </MenuList>
      </Stack>
    </CustomPopover>
  );
};
